import React from 'react';
import { PermissionKey } from '../../utils/apollo/graphqlSchema';
import usePermission from '../../utils/hooks/permissionHooks/usePermission';
import { Navigate } from 'react-router-dom';
import { appRoutes } from '../../path';
import { useBootstrapSelector } from '../../redux/selectorHooks';
import { assertNotNil } from '../../utils/types';
import useRedirectNextAvailablePage, {
  PageNameWithSubPermissions,
} from './hooks/useRedirectNextAvailablePage';
import toastMessage from '../../utils/toastMessage';

interface IProps {
  allRequiredPermissionKeys: PermissionKey[];
  validateKind?: 'AND' | 'OR';
  children: React.ReactNode;
  pageNameWithSubPermissions?: PageNameWithSubPermissions;
}

/**
 * This component is used to protect routes by permissions.
 * If permission control is disabled by Kobol, then redirect children directly.
 * @param permissionKeys - list of permission keys to check with validate kind, if any of the permission keys
 * is not permitted, then redirect to report page.
 * @param validateKind - AND vs OR permission in the list to be checked
 * @param children - children to render if all permission keys are permitted.
 */
const ProtectedRoute = ({
  allRequiredPermissionKeys,
  validateKind = 'AND',
  children,
  pageNameWithSubPermissions,
}: IProps) => {
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);
  const { checkIsPermitted } = usePermission();
  const isPermittedToViewPage = checkIsPermitted({
    requiredPermissionKeys: allRequiredPermissionKeys,
    validateKind,
  });

  const redirectPath = useRedirectNextAvailablePage({
    isPermittedToViewPage,
    pageNameWithSubPermissions,
  });
  if (redirectPath) {
    return <Navigate replace={true} to={redirectPath} />;
  }

  // ==================== Redirect to report page ====================
  /**
   * if user doesn't have permission on view page, then redirect to report page.
   * Special case: for viewing admin page, if permission control is disabled by Kobol, redirect user to report page.
   */
  const isPermissionDisabledWhenViewAdmin =
    allRequiredPermissionKeys.includes(PermissionKey.AdminRead) &&
    !permissionConfig.enablePermissionControl;
  // if no permission to read page, then redirect to report page.
  // will need to think about what if report read is disabled, then redirect to?
  if (!isPermittedToViewPage || isPermissionDisabledWhenViewAdmin) {
    if (allRequiredPermissionKeys.includes(PermissionKey.AnalysisRead)) {
      // special case, if user doesn't have permission to view report page, then redirect to Workspace page which
      // always available for users at the moment.
      toastMessage({
        message:
          'Access to the report page is denied due to insufficient permissions.',
        type: 'error',
      });
      // temp solution
      return <Navigate replace={true} to={appRoutes.system} />;
    }
    toastMessage({
      message: 'Access to the page is denied due to insufficient permissions.',
      type: 'error',
    });
    return <Navigate replace={true} to={appRoutes.report.query} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
